import { createSlice } from "@reduxjs/toolkit"
import { persistReducer } from "redux-persist"
import storage from "redux-persist/lib/storage"

import { COMPARE_IDS } from "../../../Components/Filters/CompareSelect/presets"
import { logout } from "../../LoginPage/redux/auth.actions"
import { PROGRAM_OPTIONS } from "../utils/vocabulary"
import { DEFLECT_ANALYTICS_SLICE_NAME as SLICE_NAME } from "./deflectAnalytics.constants"
import { SECTION_THUNKS } from "./deflectAnalytics.thunks"

/* ------------- Initial State ------------- */

// The calendar, the comparison and the program scope, shared by the Analytics page and its five
// "View All" pages so a card and the page behind it always cover the same window.
export const DEFAULT_DEFLECT_FILTERS = {
  // "Last 4 weeks", the same preset the Prevent overview opens on.
  period: "27 days",
  compare: { mode: COMPARE_IDS.NONE },
  lastCompare: null,
  // Program codes (cc | oi). Empty = both.
  program: []
}

// Section responses by store key (DEFLECT_SECTION). `requestIds` holds the latest request per
// key: a response that is not the latest one arrives after the merchant has moved on and is
// dropped instead of overwriting the newer answer.
const initialState = {
  filters: { ...DEFAULT_DEFLECT_FILTERS },
  data: {},
  fetching: {},
  requestIds: {}
}

/* ------------- Slice ------------- */

const slice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    changeDeflectFilters(state, { payload }) {
      state.filters = { ...state.filters, ...(payload ?? {}) }
    },
    // Another account's answers must not linger under the spinners, so its sections start from
    // skeletons again. `requestIds` stays: the requests already in flight are the new account's.
    resetDeflectData(state) {
      state.data = {}
    }
  },
  extraReducers: (builder) => {
    builder.addCase(logout, () => initialState)

    // Several keys share one endpoint (a card and its full page), so each thunk is registered
    // once and writes to whichever key the caller passed.
    new Set(Object.values(SECTION_THUNKS)).forEach((thunk) => {
      builder
        .addCase(thunk.pending, (state, { meta }) => {
          state.fetching[meta.arg.key] = true
          state.requestIds[meta.arg.key] = meta.requestId
        })
        .addCase(thunk.fulfilled, (state, { payload, meta }) => {
          const { key } = meta.arg
          if (state.requestIds[key] !== meta.requestId) return
          state.fetching[key] = false
          state.data[key] = payload
        })
        .addCase(thunk.rejected, (state, { meta }) => {
          const { key } = meta.arg
          if (state.requestIds[key] !== meta.requestId) return
          state.fetching[key] = false
          state.data[key] = null
        })
    })
  }
})

export const { changeDeflectFilters, resetDeflectData } = slice.actions

/* ------------- Selectors ------------- */

export const selectDeflectFilters = (state) => state.deflectAnalytics.filters
// How many programs the page covers: the picked ones, or every program when none is picked.
export const selectProgramCount = (state) =>
  state.deflectAnalytics.filters.program?.length || PROGRAM_OPTIONS.length
export const selectDeflectSection = (key) => (state) => state.deflectAnalytics.data[key]
export const selectDeflectFetching = (key) => (state) =>
  Boolean(state.deflectAnalytics.fetching[key])
// Loading with no answer yet: the section's skeleton. Once one has arrived (even an empty or a
// failed one), a refetch keeps it on screen under a spinner instead.
export const selectDeflectFirstLoad = (key) => (state) =>
  Boolean(state.deflectAnalytics.fetching[key]) && !(key in state.deflectAnalytics.data)

/* ------------- Persistence ------------- */
// Only the merchant's own window survives a visit: section data is refetched on every mount.

const PERSISTED_REDUCER_VERSION = 1

export const persistedReducer = persistReducer(
  {
    key: SLICE_NAME,
    storage,
    version: PERSISTED_REDUCER_VERSION,
    blacklist: ["data", "fetching", "requestIds"],
    migrate: async (state) => {
      if (state?._persist?.version < PERSISTED_REDUCER_VERSION) return undefined
      return state
    }
  },
  slice.reducer
)

export default slice.reducer
